export const OFFLINE_CACHE_PREFIX = 'aha-offline'
export const APP_STATIC_CACHE_NAME = `${OFFLINE_CACHE_PREFIX}-app-static`
export const APP_UNHASHED_CACHE_NAME = `${OFFLINE_CACHE_PREFIX}-app-unhashed`
export const REMOTE_ASSET_CACHE_NAME = `${OFFLINE_CACHE_PREFIX}-remote-assets`

export const APP_STATIC_MAX_ENTRIES = 500
export const APP_STATIC_MAX_AGE_SECONDS = 30 * 24 * 60 * 60
export const APP_UNHASHED_MAX_ENTRIES = 100
export const REMOTE_ASSET_MAX_AGE_SECONDS = 30 * 24 * 60 * 60
export const REMOTE_ASSET_BYTE_BUDGET = 250 * 1024 * 1024
export const REMOTE_ASSET_MAX_OPAQUE_ENTRIES = 1000
export const STORAGE_PRESSURE_QUOTA_FRACTION = 0.8
export const UNKNOWN_SIZE_ESTIMATED_BYTES = 512 * 1024
// Chrome pads each opaque response's quota usage to about 7 MB, whatever its real size.
export const OPAQUE_RESPONSE_QUOTA_BYTES = 7 * 1024 * 1024

export const REMOTE_ASSET_HOSTS = [
  'assets-cdn.ahaslides.com',
  'assets-cdn.dev.ahaslide.com',
  'assets-cdn-public.ahaslides.com',
  'assets-cdn-public.dev.ahaslide.com',
  'cdn.ahaslides.com',
  'd22p36lyx95rol.cloudfront.net',
  'd25510ev64x81w.cloudfront.net',
  'd2j5huodohgkpe.cloudfront.net',
  'dpttlcqctc0a1.cloudfront.net',
  'static-public.ahaslides.eu',
  'static-public.dev.ahaslides.eu',
  'static-private.ahaslides.eu',
  'static-private.dev.ahaslides.eu',
  'fonts.gstatic.com',
]

const REMOTE_ASSET_DESTINATIONS = ['image', 'font']

// Signed CDN URLs rotate these on every fetch of the presentation; the object path alone identifies the file.
const URL_SIGNING_PARAMETERS = ['Expires', 'Signature', 'Key-Pair-Id', 'Policy']

// Cached CORS responses are kept apart from opaque ones: a CORS request answered with an opaque response fails.
const REQUEST_MODE_KEY_PARAMETER = 'aha-offline-request-mode'

// First path segments served by other apps or the server (the presenter CloudFront behaviours in
// terraform sites/*/frontend.tf) and OneSignal's worker. Their files are never this app's static files.
const PATHS_OUTSIDE_THE_APP = [
  'ai',
  'api',
  'auth',
  'authen',
  'casting',
  'elearning',
  'knowledgebase',
  'manage',
  'mqtt',
  'oauth2',
  'onboarding',
  'p',
  'participants',
  'plugins',
  'push',
  'reports',
  'sandbox',
  'share',
  'slide',
  'socket.io',
  'survey',
  'team-management',
  'workspace',
]

const escapeForRegularExpression = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const ensureTrailingSlash = (pathname) => (pathname.endsWith('/') ? pathname : `${pathname}/`)

const pathRelativeToScope = (pathname, scopePath) => {
  const scope = ensureTrailingSlash(scopePath)
  if (!pathname.startsWith(scope)) return null
  return pathname.slice(scope.length)
}

const isOutsideTheApp = (relativePath) => {
  const firstSegment = relativePath.split('/')[0]
  return PATHS_OUTSIDE_THE_APP.includes(firstSegment)
}

const looksLikeAFile = (relativePath) => {
  const lastSegment = relativePath.split('/').pop()
  return /\.[a-z0-9]+$/i.test(lastSegment)
}

/**
 * The presenter routes that open from the cached shell when offline: the root redirect, the /apps
 * dashboard (Home, My presentations) and the editor. Everything else on the origin goes to the
 * network, so a path another app owns can never get this app's index.html. Tested against
 * pathname + search.
 */
export const createAppShellAllowlist = (scopePath) => {
  const scope = escapeForRegularExpression(ensureTrailingSlash(scopePath))
  return [
    new RegExp(`^${scope}(?:\\?|$)`),
    new RegExp(`^${scope}apps(?:[/?]|$)`),
    new RegExp(`^${scope}presentation/[^/?]+(?:[/?]|$)`),
  ]
}

/** Whether a navigation gets the cached shell: a top-level page load (never a frame) of an allowlisted route. */
export const isAppShellNavigation = ({
  url, request, origin, scopePath,
}) => {
  if (request.mode !== 'navigate' || request.destination !== 'document') return false
  if (url.origin !== origin) return false
  const relativePath = pathRelativeToScope(url.pathname, scopePath)
  if (relativePath === null || looksLikeAFile(relativePath)) return false
  const pathAndSearch = url.pathname + url.search
  return createAppShellAllowlist(scopePath).some((pattern) => pattern.test(pathAndSearch))
}

export const isAppStaticRequest = ({
  url, request, origin, scopePath,
}) => {
  if (request.method !== 'GET' || request.mode === 'navigate') return false
  if (url.origin !== origin) return false
  const relativePath = pathRelativeToScope(url.pathname, scopePath)
  if (relativePath === null || isOutsideTheApp(relativePath)) return false
  return looksLikeAFile(relativePath)
}

/** Build output carries a hex content hash before the extension, e.g. `app.0123abcd.js`. */
export const hasContentHash = (pathname) => /\.[0-9a-f]{8,}\.[a-z0-9]+$/i.test(pathname.split('/').pop())

export const isRemoteAssetRequest = ({ url, request }) => {
  if (request.method !== 'GET') return false
  if (!REMOTE_ASSET_HOSTS.includes(url.hostname)) return false
  return REMOTE_ASSET_DESTINATIONS.includes(request.destination)
}

export const remoteAssetCacheKey = (urlString, requestMode) => {
  const url = new URL(urlString)
  URL_SIGNING_PARAMETERS.forEach((parameter) => url.searchParams.delete(parameter))
  if (requestMode === 'cors') url.searchParams.set(REQUEST_MODE_KEY_PARAMETER, 'cors')
  return url.toString()
}

/**
 * After a deploy, an open tab can still ask for a chunk hash that is gone; the CDN then answers
 * with its HTML error page, sometimes as a 200. Caching that as a script would break every later load.
 */
export const isCacheableAppStaticResponse = (response) => {
  if (!response || response.status !== 200 || response.type === 'opaque') return false
  const contentType = response.headers.get('content-type') || ''
  return !contentType.includes('text/html')
}

export const isCacheableRemoteAssetResponse = (response) => {
  if (!response) return false
  return response.type === 'opaque' || response.status === 200
}

const estimatedResponseBytes = (response) => {
  if (response.type === 'opaque') return 0
  const contentLength = Number(response.headers.get('content-length'))
  return Number.isFinite(contentLength) && contentLength > 0 ? contentLength : UNKNOWN_SIZE_ESTIMATED_BYTES
}

/**
 * Deletes the oldest entries until the cache fits in `maxBytes`. Cache keys come back in insertion
 * order, so the newest entries are kept. Opaque responses have no measurable size and count as zero;
 * they are bounded by `enforceOpaqueEntryLimit` and `trimUnderStoragePressure` instead.
 */
export const enforceByteBudget = async (cache, maxBytes) => {
  const requests = await cache.keys()
  const sizes = await Promise.all(requests.map(async (request) => {
    const response = await cache.match(request)
    return response ? estimatedResponseBytes(response) : 0
  }))
  let keptBytes = 0
  const deletions = []
  for (let index = requests.length - 1; index >= 0; index -= 1) {
    keptBytes += sizes[index]
    if (sizes[index] > 0 && keptBytes > maxBytes) deletions.push(cache.delete(requests[index]))
  }
  await Promise.all(deletions)
  return deletions.length
}

/** Deletes the oldest opaque entries beyond `maxEntries`; measurable entries are left to the byte budget. */
export const enforceOpaqueEntryLimit = async (cache, maxEntries) => {
  const requests = await cache.keys()
  const opaqueFlags = await Promise.all(requests.map(async (request) => {
    const response = await cache.match(request)
    return Boolean(response) && response.type === 'opaque'
  }))
  const opaqueRequests = requests.filter((request, index) => opaqueFlags[index])
  const surplus = opaqueRequests.slice(0, Math.max(0, opaqueRequests.length - maxEntries))
  await Promise.all(surplus.map((request) => cache.delete(request)))
  return surplus.length
}

const quotaBytes = (response) => (response.type === 'opaque' ? OPAQUE_RESPONSE_QUOTA_BYTES : estimatedResponseBytes(response))

/**
 * When real storage use is above `quotaFraction` of the quota, deletes the oldest entries until their
 * estimated quota cost covers the excess. Deletes nothing when this whole cache could not cover it,
 * since the pressure then comes from other storage. Does nothing when `storage.estimate()` is unavailable.
 */
export const trimUnderStoragePressure = async (cache, storage, quotaFraction = STORAGE_PRESSURE_QUOTA_FRACTION) => {
  if (!storage || typeof storage.estimate !== 'function') return 0
  try {
    const { usage, quota } = await storage.estimate()
    if (!Number.isFinite(usage) || !Number.isFinite(quota) || quota <= 0) return 0
    const excessBytes = usage - quota * quotaFraction
    if (excessBytes <= 0) return 0
    const requests = await cache.keys()
    const costs = await Promise.all(requests.map(async (request) => {
      const response = await cache.match(request)
      return response ? quotaBytes(response) : 0
    }))
    if (costs.reduce((total, cost) => total + cost, 0) < excessBytes) return 0
    let freedBytes = 0
    const oldestEntries = []
    for (let index = 0; index < requests.length && freedBytes < excessBytes; index += 1) {
      freedBytes += costs[index]
      oldestEntries.push(requests[index])
    }
    await Promise.all(oldestEntries.map((request) => cache.delete(request)))
    return oldestEntries.length
  } catch (error) {
    return 0
  }
}
